<template>
  <b-card>
    <div class="d-flex flex-wrap align-items-center mb-1">
      <h4 class="mb-0 mr-1">Запустить процесс</h4>
      <b-button
        size="sm"
        variant="flat-secondary"
        @click="loadProcesses"
      >
        <feather-icon icon="RefreshCwIcon" /> Обновить
      </b-button>
      <div class="flex-grow-1" />
      <b-input-group
        size="sm"
        style="max-width: 280px"
      >
        <b-input-group-prepend is-text>
          <feather-icon icon="SearchIcon" />
        </b-input-group-prepend>
        <b-form-input
          v-model="search"
          placeholder="Поиск процесса…"
          autocomplete="off"
        />
        <b-input-group-append
          v-if="search"
          is-text
          class="cursor-pointer"
          @click="search = ''"
        >
          <feather-icon icon="XIcon" />
        </b-input-group-append>
      </b-input-group>
    </div>

    <p class="text-muted">
      Выберите процесс, который нужно запустить. Появится новое дело (экземпляр), вы станете его инициатором,
      а первый этап уйдёт исполнителям. Показаны только опубликованные процессы, доступные вам по роли.
    </p>

    <b-table
      :items="filtered"
      :fields="fields"
      responsive
      hover
      small
      :busy="loading"
      :per-page="perPage"
      :current-page="currentPage"
      show-empty
      :empty-text="processes.length ? 'Ничего не найдено по запросу.' : 'Нет доступных для запуска процессов.'"
      :empty-filtered-text="'Ничего не найдено по запросу.'"
      class="mb-0"
    >
      <template #table-busy>
        <div class="text-center text-primary my-2">
          <b-spinner small class="align-middle mr-50" /> Загрузка…
        </div>
      </template>
      <template #cell(name)="data">
        <span class="font-weight-bolder">{{ data.item.name }}</span>
      </template>
      <template #cell(descr)="data">
        <span class="text-muted">{{ data.item.descr || '—' }}</span>
      </template>
      <template #cell(version)="data">
        <b-badge variant="light-secondary">v{{ data.item.version }}</b-badge>
      </template>
      <template #cell(actions)="data">
        <b-button
          size="sm"
          variant="success"
          @click="openLaunch(data.item)"
        >
          <feather-icon icon="PlayIcon" /> Запустить
        </b-button>
      </template>
    </b-table>
    <b-pagination
      v-if="filtered.length > perPage"
      v-model="currentPage"
      :total-rows="filtered.length"
      :per-page="perPage"
      align="center"
      size="sm"
      class="mt-1"
    />

    <!-- Подтверждение запуска: несёт смысл «реальный прогон» -->
    <b-modal
      v-model="launch.show"
      title="Запустить процесс?"
      centered
      hide-footer
    >
      <template v-if="!launch.startedId">
        <p class="mb-50">
          Процесс: <b>{{ launch.proc && launch.proc.name }}</b> <span class="text-muted">(v{{ launch.proc && launch.proc.version }})</span>
        </p>
        <p class="mb-50">
          Вы станете <b>инициатором</b>: {{ launch.initiatorName }}
        </p>
        <p class="mb-25">
          Первый этап «<b>{{ launch.entryLabel || '…' }}</b>» получит задачу:
        </p>
        <ul class="mb-50 pl-2">
          <li
            v-for="(u, ui) in (launch.preview.users || [])"
            :key="`lu${ui}`"
          >
            {{ u }}
          </li>
          <li
            v-for="(n, ni) in (launch.preview.notes || [])"
            :key="`ln${ni}`"
            class="text-muted"
          >
            {{ n }}
          </li>
        </ul>
        <p class="small text-warning mb-1">
          <feather-icon icon="AlertTriangleIcon" /> Этим людям придёт задача в «Мои задачи». Это реальный прогон.
        </p>
        <div class="d-flex justify-content-end">
          <b-button
            variant="outline-secondary"
            class="mr-1"
            @click="launch.show = false"
          >
            Отмена
          </b-button>
          <b-button
            variant="success"
            :disabled="launch.loading"
            @click="confirmLaunch"
          >
            <feather-icon icon="PlayIcon" /> {{ launch.loading ? 'Запускаю…' : 'Запустить' }}
          </b-button>
        </div>
      </template>
      <template v-else>
        <p class="mb-1">
          <feather-icon
            icon="CheckCircleIcon"
            class="text-success"
          /> Экземпляр <b>#{{ launch.startedId }}</b> запущен.
        </p>
        <div class="d-flex justify-content-end">
          <b-button
            variant="outline-primary"
            class="mr-1"
            @click="goAfterLaunch('cubus-bpm-monitor')"
          >
            Открыть в мониторинге
          </b-button>
          <b-button
            variant="primary"
            @click="goAfterLaunch('cubus-bpm-inbox')"
          >
            Мои задачи
          </b-button>
        </div>
      </template>
    </b-modal>
  </b-card>
</template>

<script>
import {
  BCard, BTable, BButton, BBadge, BModal, BSpinner, BPagination,
  BFormInput, BInputGroup, BInputGroupPrepend, BInputGroupAppend,
} from 'bootstrap-vue'
import useJwt from '@/cubus/jwt/useJwt'
import useCubus from '@/cubus/services/useCubus'

export default {
  name: 'CbsBpmLaunch',
  components: {
    BCard, BTable, BButton, BBadge, BModal, BSpinner, BPagination,
    BFormInput, BInputGroup, BInputGroupPrepend, BInputGroupAppend,
  },
  data() {
    return {
      loading: false,
      currentPage: 1,
      perPage: 10,
      processes: [],
      search: '',
      fields: [
        { key: 'name', label: 'Процесс' },
        { key: 'descr', label: 'Описание' },
        { key: 'version', label: 'Версия', class: 'text-center', thStyle: { width: '80px' } },
        { key: 'actions', label: '', class: 'text-right', thStyle: { width: '140px' } },
      ],
      launch: {
        show: false,
        loading: false,
        startedId: null,
        proc: null,
        entryLabel: '',
        initiatorName: 'вы',
        preview: { users: [], notes: [] },
      },
    }
  },
  computed: {
    filtered() {
      const q = this.search.trim().toLowerCase()
      if (!q) return this.processes
      return this.processes.filter(p => `${p.name || ''} ${p.descr || ''}`.toLowerCase().includes(q))
    },
  },
  watch: {
    search() { this.currentPage = 1 },
  },
  created() {
    useCubus.setPageTitle('Запустить процесс')
    this.loadProcesses()
  },
  methods: {
    api(method, param) {
      return useJwt.query({
        token: useJwt.getCubusToken(),
        query: { method, param, mode: 'sync' },
      }).then(r => {
        if (r.data.error) { useCubus.toastError(this, r.data.error); return null }
        return r.data.result
      }).catch(e => { useCubus.toastError(this, e); return null })
    },
    loadProcesses() {
      this.loading = true
      this.api('bpm_startable', {}).then(r => { this.loading = false; if (r) this.processes = r.processes || [] })
    },
    openLaunch(p) {
      const sess = this.$store.getters['cubus-store/SESSION']
      this.launch.proc = p
      this.launch.startedId = null
      this.launch.loading = false
      this.launch.entryLabel = ''
      this.launch.initiatorName = (sess && sess.user && (sess.user.name || sess.user.username)) || 'вы'
      this.launch.preview = { users: [], notes: [] }
      this.launch.show = true
      // подтянуть стартовый этап и показать, кому уйдёт первая задача
      this.api('bpm_schema', { process_def_id: p.id }).then(r => {
        if (!r || !r.process) return
        const entryId = r.process.entry_stage_id
        const entry = (r.stages || []).find(s => s.id === entryId)
        this.launch.entryLabel = entry ? entry.label : '—'
        this.api('bpm_assignees_preview', { expr: entry ? (entry.assignees_expr || '') : '' }).then(pr => {
          if (pr) this.launch.preview = pr.preview || { users: [], notes: [] }
        })
      })
    },
    confirmLaunch() {
      if (!this.launch.proc) return
      this.launch.loading = true
      this.api('bpm_start', { process_def_id: this.launch.proc.id }).then(r => {
        this.launch.loading = false
        if (r && r.instance_id) {
          this.launch.startedId = r.instance_id
          useCubus.toastMessage(this, `Экземпляр #${r.instance_id} запущен`)
        }
      })
    },
    goAfterLaunch(routeName) {
      this.launch.show = false
      this.$router.push({ name: routeName }).catch(() => {})
    },
  },
}
</script>
